import {
  addDoc, arrayRemove, arrayUnion, collection, doc, getDocs, limit, onSnapshot, orderBy, query, updateDoc, where,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { docsWithId, logListenerError, withId } from './db';

const postsCollection = () => collection(db, 'posts');
const postRef = (id) => doc(db, 'posts', id);
const commentsCollection = (postId) => collection(db, 'posts', postId, 'comments');

/** Live list of all posts. Returns an unsubscribe function. */
export function subscribePosts(onChange, onError = logListenerError('posts')) {
  return onSnapshot(postsCollection(), (snapshot) => onChange(docsWithId(snapshot)), onError);
}

/** Live list of the `max` newest posts. */
export function subscribeRecentPosts(max, onChange, onError = logListenerError('posts')) {
  return onSnapshot(query(postsCollection(), orderBy('time', 'desc'), limit(max)),
    (snapshot) => onChange(docsWithId(snapshot)), onError);
}

/** Live single post ({ id, ...data } or null). */
export function subscribePost(id, onChange, onError = logListenerError('post')) {
  return onSnapshot(postRef(id), (snap) => onChange(withId(snap)), onError);
}

export async function listPosts() {
  return docsWithId(await getDocs(postsCollection()));
}

/** Posts by author username (older posts only record the username). */
export async function listPostsByCreatorName(username) {
  return docsWithId(await getDocs(query(postsCollection(), where('creator', '==', username))));
}

/** Posts the given profile has saved. */
export async function listPostsSavedBy(profileId) {
  return docsWithId(await getDocs(query(postsCollection(), where('savedBy', 'array-contains', profileId))));
}

export function createPost({ content, creator, creatorId, media = [] }) {
  return addDoc(postsCollection(), { content, creator, creatorId, media, time: new Date().getTime().toString() });
}

export function updatePost(id, data) {
  return updateDoc(postRef(id), data);
}

const toggle = (field) => (postId, profileId, on) => updatePost(postId, { [field]: on ? arrayUnion(profileId) : arrayRemove(profileId) });

/** Add or remove the profile's like / repost / save on a post. */
export const setPostLiked = toggle('likedBy');
export const setPostReposted = toggle('repostedBy');
export const setPostSavedBy = toggle('savedBy');

export function reportPost({ postId, reportedBy, reporterUsername }) {
  return addDoc(collection(db, 'reportedPosts'), { postId, reportedBy, reporterUsername, createdAt: Date.now() });
}

// ─── Comments ───────────────────────────────────────────────────────

/** Live comments on a post, oldest first. */
export function subscribeComments(postId, onChange, onError = logListenerError('comments')) {
  return onSnapshot(commentsCollection(postId), (snapshot) => {
    onChange(docsWithId(snapshot).sort((a, b) => Number(a.time || 0) - Number(b.time || 0)));
  }, onError);
}

/** Live comment count for a post. */
export function subscribeCommentCount(postId, onChange, onError = logListenerError('comment count')) {
  return onSnapshot(commentsCollection(postId), (snapshot) => onChange(snapshot.size), onError);
}

export function addComment(postId, { content, creator, creatorId, parentId = null }) {
  return addDoc(commentsCollection(postId), { content, creator, creatorId, parentId, time: Date.now() });
}

export function setCommentLiked(postId, commentId, profileId, liked) {
  return updateDoc(doc(db, 'posts', postId, 'comments', commentId), {
    likedBy: liked ? arrayUnion(profileId) : arrayRemove(profileId),
  });
}
